import { inject, Injectable } from '@angular/core';
import { ApiDataAccess, interpolate } from '@softline/core';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  SOFTLINE_API_ABAU_ARCHIVE_DOWNLOAD,
  SOFTLINE_API_ABAU_DOCUMENT_HEAD,
} from '../invoice-link.api';
import { DocumentVariant, KopfBelegResult } from '../types/archive';

@Injectable()
export class ArchiveDocumentRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  /** Step 1: Belegkopf incl. fileKey. `documentId` is the RAW number (prefix stripped). */
  loadDocumentHead(
    documentId: number,
    variant: DocumentVariant
  ): Promise<KopfBelegResult> {
    return this.dataAccess.get<KopfBelegResult>({
      path: interpolate(SOFTLINE_API_ABAU_DOCUMENT_HEAD, { id: documentId }),
      queryParams: { belegvariante: variant },
    });
  }

  /** Step 2: the archived document; the file type comes from the blob's content type. */
  async download(fileKey: string): Promise<Blob> {
    const events = this.dataAccess.download<Blob>({
      path: interpolate(SOFTLINE_API_ABAU_ARCHIVE_DOWNLOAD, {
        fileKey: encodeURIComponent(fileKey),
      }),
    });
    // The converter maps only sent/progress/response and lets every other HTTP event
    // through as `undefined` — the response header among them — so guard the access.
    const event = await firstValueFrom(
      events.pipe(filter((e) => e?.type === 'response'))
    );
    const body = event?.type === 'response' ? event.response : null;
    if (body instanceof Blob) return body;
    if (body instanceof ArrayBuffer) return new Blob([body]);
    throw new Error('archive download returned no content');
  }
}
